import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, isUserNotFoundError } from '../utils/auth';

// ═══════════════════════════════════════════════════════════════
// Palette (same as Login/Dashboard)
// ═══════════════════════════════════════════════════════════════
const PALETTES = {
  B: {
    key: 'B', primary: '#FF5C5C', primaryShadow: '#B83838',
    accent: '#FFD23F', accent2: '#3DC9B5',
    sunset: '#FF8A3D', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5',
    ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4',
    mutedText: '#7A6B85',
    heroStart: '#FFB86B', heroEnd: '#FF5C5C',
  },
  A: {
    key: 'A', primary: '#2A6B30', primaryShadow: '#1E5C2E',
    accent: '#E8A020', accent2: '#67A844',
    sunset: '#E8A020', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844',
    ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6',
    mutedText: '#A67C52',
    heroStart: '#EAF2EA', heroEnd: '#1E5C2E',
  },
  C: {
    key: 'C', primary: '#5B4EC7', primaryShadow: '#3B2F9E',
    accent: '#FFD23F', accent2: '#16A085',
    sunset: '#8B7CF6', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085',
    ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF',
    mutedText: '#6A6A80',
    heroStart: '#B8A9FF', heroEnd: '#5B4EC7',
  },
};
const FONT = '"Nunito", "Baloo 2", sans-serif';

// Owl emoji placeholder
const Owl = ({ size = 60 }) => (
  <div style={{ fontSize: size * 0.85, lineHeight: 1 }}>🦉</div>
);

// ═══════════════════════════════════════════════════════════════
// Shared Shell (top bar with step progress)
// ═══════════════════════════════════════════════════════════════
const OnbShell = ({ step, totalSteps, canSkip, onSkip, P, children }) => (
  <div style={{ width: '100%', minHeight: '100vh', background: P.cream, fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
    {/* Top bar */}
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px',
      background: '#fff', borderBottom: `3px solid ${P.ink}`, flexWrap: 'wrap',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', background: '#fff',
        border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Owl size={30} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: P.ink }}>HuPath</div>
      <div style={{ width: 1, height: 22, background: P.ink, opacity: 0.2 }} />
      <div style={{
        background: P.accent, color: P.ink,
        border: `2px solid ${P.ink}`, borderRadius: 8,
        padding: '4px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
      }}>🎒 HỌC VIÊN MỚI</div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 6, minWidth: 100 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{
            width: i < step ? 32 : 20, height: 8, borderRadius: 4,
            background: i < step ? P.coral : (i === step ? P.plum : P.cream),
            border: `2px solid ${P.ink}`, transition: 'all 0.3s',
          }} />
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, opacity: 0.6 }}>
        Bước {step + 1}/{totalSteps}
      </div>
      {canSkip && (
        <button onClick={onSkip} style={{
          padding: '6px 14px', border: `2px solid ${P.ink}`, borderRadius: 8,
          background: '#fff', color: P.ink, fontFamily: FONT, fontSize: 12, fontWeight: 800,
          cursor: 'pointer',
        }}>Bỏ qua →</button>
      )}
    </div>
    <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Palette switcher (shared)
// ═══════════════════════════════════════════════════════════════
const PaletteSwitcher = ({ current, onChange }) => (
  <div style={{
    position: 'fixed', top: 16, right: 16, zIndex: 1000,
    display: 'flex', gap: 8, alignItems: 'center',
    background: 'rgba(255,255,255,0.95)', padding: '8px 12px', borderRadius: 999,
    border: '2px solid rgba(0,0,0,0.15)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    fontFamily: 'system-ui, sans-serif',
  }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Màu:</span>
    {Object.values(PALETTES).map(p => {
      const active = current === p.key;
      return (
        <button key={p.key} onClick={() => onChange(p.key)} title={p.key} style={{
          width: 26, height: 26, borderRadius: '50%',
          background: `linear-gradient(135deg, ${p.heroStart}, ${p.heroEnd})`,
          border: active ? `3px solid ${p.ink}` : '2px solid rgba(0,0,0,0.15)',
          boxShadow: active ? `0 0 0 2px #fff` : 'none',
          cursor: 'pointer', padding: 0,
          transition: 'all 0.15s', transform: active ? 'scale(1.1)' : 'scale(1)',
        }} />
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Avatars (kept from original)
// ═══════════════════════════════════════════════════════════════
const AVATARS = [
  { key: '🦁', name: 'Sư tử', emoji: '🦁' },
  { key: '🐼', name: 'Gấu trúc', emoji: '🐼' },
  { key: '🦊', name: 'Cáo', emoji: '🦊' },
  { key: '🐨', name: 'Koala', emoji: '🐨' },
  { key: '🦄', name: 'Kỳ lân', emoji: '🦄' },
  { key: '🐯', name: 'Hổ', emoji: '🐯' },
];

const LEVEL_META = {
  Starters: { emoji: '🐣', age: '6–8', desc: 'Bé mới làm quen tiếng Anh', bg: '#CFE8F5' },
  Movers:   { emoji: '🐥', age: '8–10', desc: 'Đã biết từ vựng cơ bản', bg: '#F4E4A8' },
  Flyers:   { emoji: '🦅', age: '10–12', desc: 'Có thể đọc hiểu đoạn ngắn', bg: '#FFD9B8' },
};

// ═══════════════════════════════════════════════════════════════
// Main Onboarding component
// ═══════════════════════════════════════════════════════════════
const Onboarding = () => {
  const navigate = useNavigate();
  const P = PALETTES.B;

  const [step, setStep] = useState(0); // 0 = welcome, 1 = pick level + avatar
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const userName = localStorage.getItem('userFullName') || 'Bạn';

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/levels/?skip=0&limit=10`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, accept: 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          setLevels(data.data || data);
        } else if (response.status === 404) {
          const errorData = await response.json();
          if (isUserNotFoundError(response, errorData)) { logout(navigate); return; }
        }
      } catch (err) { console.error('Failed to fetch levels:', err); }
    };
    fetchLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!selectedLevel || !selectedAvatar) return;
    setIsLoading(true); setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/client/onboarding/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          accept: 'application/json',
        },
        body: JSON.stringify({ level_id: selectedLevel, avatar: selectedAvatar }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (isUserNotFoundError(response, errorData)) { logout(navigate); return; }
        throw new Error(errorData.detail || 'Lỗi khi lưu thông tin');
      }
      localStorage.setItem('userAvatar', selectedAvatar);
      navigate('/placement-test-intro');
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  const handleSkip = () => navigate('/dashboard');

  // ═══════════════════════════════════════════════════════════════
  // STEP 0 — WELCOME
  // ═══════════════════════════════════════════════════════════════
  if (step === 0) {
    return (
      <>
        <OnbShell step={0} totalSteps={4} canSkip={false} P={P}>
          <div style={{
            minHeight: 'calc(100vh - 80px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `radial-gradient(ellipse at 50% 30%, ${P.sand} 0%, ${P.cream} 70%)`,
            position: 'relative', padding: 40,
          }}>
            {/* Confetti decorations */}
            {['🎉', '⭐', '✨', '🎊', '⭐', '✨', '🌟', '🎈'].map((e, i) => (
              <div key={i} style={{
                position: 'absolute',
                top: `${6 + (i * 11) % 82}%`, left: `${4 + (i * 17) % 92}%`,
                fontSize: 22 + (i % 3) * 10, opacity: 0.35,
                transform: `rotate(${i * 37}deg)`, pointerEvents: 'none',
              }}>{e}</div>
            ))}

            <div style={{
              background: '#fff', borderRadius: 28, padding: '40px 56px',
              border: `3.5px solid ${P.ink}`, boxShadow: `0 10px 0 ${P.ink}`,
              maxWidth: 680, width: '100%', textAlign: 'center', position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: -32, left: '50%', transform: 'translateX(-50%)',
                background: P.accent, border: `3px solid ${P.ink}`, borderRadius: '50%',
                padding: 8, boxShadow: `0 5px 0 ${P.ink}`,
              }}>
                <Owl size={72} />
              </div>

              <div style={{ marginTop: 40, fontSize: 13, fontWeight: 800, color: P.plum, letterSpacing: 3 }}>
                CHÀO MỪNG ĐẾN VỚI HUPATH
              </div>
              <div style={{
                fontSize: 44, fontWeight: 800, color: P.ink, letterSpacing: -1.5,
                marginTop: 10, lineHeight: 1.08,
              }}>
                Xin chào, <span style={{ color: P.coral }}>{userName}</span>! 👋
              </div>
              <div style={{ fontSize: 17, fontWeight: 600, color: P.ink, opacity: 0.75, marginTop: 14, lineHeight: 1.5 }}>
                Hu sẽ giúp bé tìm lộ trình học phù hợp qua một bài kiểm tra nhanh — chỉ khoảng{' '}
                <b style={{ background: P.accent, padding: '0 4px', borderRadius: 4 }}>5–10 phút</b>.
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => setStep(1)} style={{
                  padding: '16px 34px', border: `3px solid ${P.ink}`,
                  background: P.coral, color: '#fff', fontFamily: FONT, fontSize: 17, fontWeight: 800,
                  borderRadius: 16, boxShadow: `0 6px 0 ${P.primaryShadow}`, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>Bắt đầu hành trình <span>→</span></button>
                <button onClick={() => {
                  localStorage.setItem('hasCompletedPlacement', 'true');
                  navigate('/dashboard');
                }} style={{
                  padding: '16px 24px', border: `3px solid ${P.ink}`,
                  background: '#fff', color: P.ink, fontFamily: FONT, fontSize: 14, fontWeight: 800,
                  borderRadius: 16, boxShadow: `0 6px 0 ${P.ink}`, cursor: 'pointer',
                }}>Khám phá tự do</button>
              </div>

              <div style={{
                display: 'flex', gap: 20, marginTop: 32, justifyContent: 'center',
                fontSize: 12, fontWeight: 700, color: P.ink, opacity: 0.6, flexWrap: 'wrap',
              }}>
                <div>✅ 5–10 phút</div><div>•</div>
                <div>🎯 Gợi ý cá nhân hoá</div><div>•</div>
                <div>🏆 +50 Hu Coins</div>
              </div>
            </div>
          </div>
        </OnbShell>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // STEP 1 — LEVEL + AVATAR picker
  // ═══════════════════════════════════════════════════════════════
  return (
    <>
      <OnbShell step={1} totalSteps={4} canSkip={true} onSkip={handleSkip} P={P}>
        <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto' }}>
          {/* Level section */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: P.plum, letterSpacing: 3 }}>
              BƯỚC 2 · CHỌN TRÌNH ĐỘ
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: P.ink, letterSpacing: -1, marginTop: 6 }}>
              {userName} đang ở đâu? 🗺️
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: P.ink, opacity: 0.65, marginTop: 6 }}>
              Chọn trình độ Cambridge YLE gần nhất — có thể đổi sau nếu cần
            </div>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 18,
          }}>
            {levels.map(l => {
              const meta = LEVEL_META[l.name] || { emoji: '📚', age: '—', desc: '', bg: P.sand };
              const selected = selectedLevel === l.id;
              return (
                <div key={l.id} onClick={() => setSelectedLevel(l.id)} style={{
                  background: selected ? meta.bg : '#fff',
                  border: `3.5px solid ${selected ? P.coral : P.ink}`,
                  boxShadow: selected ? `0 8px 0 ${P.coral}` : `0 6px 0 ${P.ink}`,
                  borderRadius: 20, padding: '22px 18px',
                  position: 'relative', cursor: 'pointer',
                  transform: selected ? 'translateY(-4px)' : 'none',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}>
                  {selected && (
                    <div style={{
                      position: 'absolute', top: -12, right: -12,
                      width: 34, height: 34, borderRadius: '50%',
                      background: P.coral, color: '#fff',
                      border: `3px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, fontWeight: 800,
                    }}>✓</div>
                  )}
                  <div style={{
                    width: 76, height: 76, borderRadius: 18, margin: '0 auto',
                    background: meta.bg, border: `2.5px solid ${P.ink}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 42,
                  }}>{meta.emoji}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: P.ink, textAlign: 'center', marginTop: 14 }}>{l.name}</div>
                  <div style={{
                    textAlign: 'center', marginTop: 4,
                    background: selected ? '#fff' : P.cream,
                    border: `2px solid ${P.ink}`, borderRadius: 8,
                    padding: '2px 10px', width: 'fit-content', margin: '6px auto',
                    fontSize: 10, fontWeight: 800, color: P.plum, letterSpacing: 1,
                  }}>{meta.age} tuổi</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: P.ink, opacity: 0.75, textAlign: 'center', lineHeight: 1.4, marginTop: 8 }}>
                    {meta.desc}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Avatar section */}
          <div style={{ marginTop: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: P.plum, letterSpacing: 3 }}>
              CHỌN AVATAR
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: P.ink, marginTop: 4 }}>
              Con muốn làm bạn với ai? 🎭
            </div>
          </div>

          <div style={{
            marginTop: 20, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: 12, maxWidth: 780, marginInline: 'auto',
          }}>
            {AVATARS.map(a => {
              const selected = selectedAvatar === a.key;
              return (
                <button key={a.key} onClick={() => setSelectedAvatar(a.key)} style={{
                  background: selected ? P.accent : '#fff',
                  border: `3px solid ${P.ink}`,
                  boxShadow: selected ? `0 6px 0 ${P.coral}` : `0 4px 0 ${P.ink}`,
                  borderRadius: 16, padding: '14px 10px', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  transform: selected ? 'translateY(-3px)' : 'none',
                  transition: 'all 0.15s', fontFamily: FONT,
                }}>
                  <div style={{ fontSize: 44, lineHeight: 1 }}>{a.emoji}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: P.ink }}>{a.name}</div>
                </button>
              );
            })}
          </div>

          {/* Error */}
          {error && (
            <div style={{
              marginTop: 20, padding: '12px 16px', background: '#FFE4E4',
              border: `2px solid ${P.coral}`, borderRadius: 12,
              color: P.primaryShadow, fontSize: 13, fontWeight: 700,
              maxWidth: 500, marginInline: 'auto', textAlign: 'center',
            }}>⚠ {error}</div>
          )}

          {/* CTA */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => setStep(0)} style={{
              padding: '14px 24px', border: `3px solid ${P.ink}`, borderRadius: 16,
              background: '#fff', color: P.ink,
              fontFamily: FONT, fontSize: 14, fontWeight: 800,
              boxShadow: `0 5px 0 ${P.ink}`, cursor: 'pointer',
            }}>← Quay lại</button>
            <button onClick={handleSubmit} disabled={!selectedLevel || !selectedAvatar || isLoading} style={{
              padding: '14px 36px', border: `3px solid ${P.ink}`, borderRadius: 16,
              background: (selectedLevel && selectedAvatar) ? P.coral : '#ccc',
              color: '#fff', fontFamily: FONT, fontSize: 16, fontWeight: 800,
              boxShadow: (selectedLevel && selectedAvatar) ? `0 6px 0 ${P.primaryShadow}` : 'none',
              cursor: (selectedLevel && selectedAvatar && !isLoading) ? 'pointer' : 'not-allowed',
              opacity: isLoading ? 0.75 : 1,
            }}>
              {isLoading ? 'Đang lưu...' : 'Tiếp tục →'}
            </button>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
            marginTop: 16, fontSize: 12, fontWeight: 700, color: P.mutedText,
          }}>
            💡 Bước tiếp theo: Bài kiểm tra đầu vào (15 câu · 10 phút)
          </div>
        </div>
      </OnbShell>
    </>
  );
};

export default Onboarding;
